import React, { Component, ErrorInfo, ReactNode } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, ScrollView } from 'react-native';
import { useThemeSafe } from '../context/themeContext';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

interface FallbackProps {
  error: Error | null;
  onReset: () => void;
}

function ErrorFallback({ error, onReset }: FallbackProps) {
  const { theme } = useThemeSafe();
  const { colors } = theme;

  return (
    <View style={[styles.container, { backgroundColor: colors.paper }]}>
      <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
        <View style={[styles.iconCircle, { backgroundColor: colors.dangerSoft }]}>
          <Text style={styles.icon}>⚠️</Text>
        </View>
        <Text style={[styles.title, { color: colors.ink }]}>Something went wrong</Text>
        <Text style={[styles.message, { color: colors.ink3 }]}>
          The application encountered an unexpected error. This might be due to a temporary connection issue or a corrupted session.
        </Text>

        {(typeof __DEV__ !== 'undefined' && __DEV__) && (
          <ScrollView style={[styles.errorScroll, { backgroundColor: colors.paper2 }]}>
            <Text style={[styles.errorText, { color: colors.danger }]}>{error?.toString()}</Text>
          </ScrollView>
        )}

        <TouchableOpacity
          style={[styles.button, { backgroundColor: colors.accent }]}
          onPress={onReset}
          activeOpacity={0.8}
        >
          <Text style={[styles.buttonText, { color: colors.accentInk }]}>Refresh Application</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

export default class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    if (Platform.OS === 'web') {
      window.location.reload();
    }
  };

  public render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} onReset={this.handleReset} />;
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    ...(Platform.OS === 'web' ? ({ minHeight: '100vh' } as any) : {}),
  },
  card: {
    borderRadius: 24,
    padding: 32,
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
    borderWidth: 1,
  },
  iconCircle: {
    width: 64,
    height: 64,
    borderRadius: 32,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  icon: {
    fontSize: 32,
  },
  title: {
    fontSize: 22,
    fontWeight: '800',
    marginBottom: 12,
    textAlign: 'center',
  },
  message: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 24,
  },
  button: {
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 12,
    width: '100%',
    alignItems: 'center',
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}),
  },
  buttonText: {
    fontSize: 15,
    fontWeight: '700',
  },
  errorScroll: {
    maxHeight: 150,
    width: '100%',
    borderRadius: 8,
    padding: 10,
    marginBottom: 20,
  },
  errorText: {
    fontSize: 12,
    fontFamily: Platform.OS === 'web' ? 'monospace' : undefined,
  },
});
